import { isValidElement } from 'react';
import { Card, Grid2 as Grid, Stack, Typography } from '@mui/material';
import ObjectDetailsHeader from './ObjectDetailsHeader';
import { cardContentSx, cardSx, flexColumnSx, flexItemSx, scrollableCardContentSx } from './styles';
import type { ObjectDetailsContent, ObjectDetailsContentLayoutProps } from './types';
import LoadError from '../../components/LoadError';
import { mergeSx } from 'framework/utils';

const renderRightSide = (rightSide: ObjectDetailsContent['rightSide']) => {
  const content = (Array.isArray(rightSide) ? rightSide : [rightSide]).filter(item => Boolean(item));
  return content.map((item, index) => {
    const isLastItem = index === content.length - 1;

    return (
      <Card
        key={isValidElement(item) && item.key ? item.key : index}
        sx={{
          ...cardSx,
          flex: isLastItem ? '1 1 0' : '0 0 auto',
        }}
      >
        <Stack sx={isLastItem ? scrollableCardContentSx : cardContentSx}>{item}</Stack>
      </Card>
    );
  });
};

const ObjectDetailsContentLayout = <TData,>({
  data,
  loading,
  error,
  retry,
  notFoundMessage,
  renderContent,
  sx,
}: ObjectDetailsContentLayoutProps<TData>) => {
  const isDataMissing = data === null || data === undefined;

  if (isDataMissing) {
    if (error) return <LoadError message="Could not load details." retry={retry} loading={loading} />;
    return loading ? null : (
      <Stack sx={{ p: 2 }}>
        <Typography color="textSecondary">{notFoundMessage}</Typography>
      </Stack>
    );
  }

  const { header, leftSide, rightSide } = renderContent(data);

  return (
    <Stack gap={1} sx={mergeSx(sx, flexColumnSx)}>
      {error && (
        <LoadError
          message="Could not refresh details. Showing previously loaded data."
          retry={retry}
          loading={loading}
        />
      )}
      {header && <ObjectDetailsHeader {...header} />}
      <Grid container spacing={1} sx={{ ...flexColumnSx, alignItems: 'stretch' }}>
        <Grid size={{ xs: 12, md: 5 }} sx={{ ...flexItemSx, minWidth: 0 }}>
          <Card sx={{ ...cardSx, flex: '1 1 0' }}>
            <Stack sx={scrollableCardContentSx}>{leftSide}</Stack>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }} sx={flexItemSx}>
          <Stack gap={1} sx={flexColumnSx}>
            {renderRightSide(rightSide)}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ObjectDetailsContentLayout;
